import { createContext, useCallback, useContext, useEffect, useRef, useState } from "react";

// Сховище відповідей для режиму ДЗ (і перегляду ДЗ вчителем).
// Без провайдера (презентація на уроці) блоки працюють як раніше — стан живе лише в слайді.
export const AnswersContext = createContext(null);

// Спільний слайд у кімнаті уроку Speaksy (VIDEO1): що клацнув вчитель чи учень — бачать обидва, аудіо/відео грає в обох.
// Провайдер є лише в кімнаті дзвінка. У режимі презентації (Meet) і в ДЗ його немає — блоки працюють як раніше.
// sync: { send(slideId, key, value), listen(slideId, key, cb) → off, sendMedia(id, action, time), listenMedia(id, cb) → off }
export const SyncContext = createContext(null);

const resolve = (next, prev) => (typeof next === "function" ? next(prev) : next);

// Значення з іншого боку кімнати → у сховище і в стан блока (без повторної відправки)
function useRemote(sync, ctx, slideId, key, setValue) {
  useEffect(() => {
    if (!sync) return undefined;
    return sync.listen(slideId, key, (value) => {
      ctx?.save(slideId, key, value);
      setValue(value);
    });
  }, [sync, ctx, slideId, key, setValue]);
}

// Стан блока, який переживає перегортання слайдів і відновлюється зі збережених відповідей
export function useSlideState(slideId, key, initial) {
  const ctx = useContext(AnswersContext);
  const sync = useContext(SyncContext);
  const [value, setValue] = useState(() => ctx?.values[slideId]?.[key] ?? initial);
  const update = useCallback(
    (next) =>
      setValue((prev) => {
        const resolved = resolve(next, prev);
        ctx?.save(slideId, key, resolved);
        sync?.send(slideId, key, resolved);
        return resolved;
      }),
    [ctx, sync, slideId, key]
  );
  useRemote(sync, ctx, slideId, key, setValue);
  return [value, update];
}

// Стан інтерфейсу слайда (перевернута картка, номер питання): у кімнаті уроку спільний, деінде — звичайний useState.
// У відповіді ДЗ не потрапляє (без SyncContext сховище не чіпаємо)
export function useSharedState(slideId, key, initial) {
  const ctx = useContext(AnswersContext);
  const sync = useContext(SyncContext);
  const k = `ui:${key}`;
  const [value, setValue] = useState(() => (sync ? ctx?.values[slideId]?.[k] ?? initial : initial));
  const update = useCallback(
    (next) =>
      setValue((prev) => {
        const resolved = resolve(next, prev);
        if (sync) {
          ctx?.save(slideId, k, resolved);
          sync.send(slideId, k, resolved);
        }
        return resolved;
      }),
    [ctx, sync, slideId, k]
  );
  useRemote(sync, ctx, slideId, k, setValue);
  return [value, update];
}

// Аудіо/відео в кімнаті уроку: play / pause / перемотування в одного — те саме в іншого.
// Повертає true, якщо браузер учня не дав запустити звук сам (треба один раз натиснути ▶)
export function useMediaSync(id, ref) {
  const sync = useContext(SyncContext);
  const [blocked, setBlocked] = useState(false);
  const quietUntil = useRef(0);
  useEffect(() => {
    const el = ref.current;
    if (!sync || !el || !id) return undefined;
    // події, які викликали ми самі (застосовуємо чужу дію), назад не відправляємо
    const send = (action) => {
      if (Date.now() < quietUntil.current) return;
      sync.sendMedia(id, action, el.currentTime);
      if (action === "play") setBlocked(false);
    };
    const onPlay = () => send("play");
    const onPause = () => send("pause");
    const onSeeked = () => send("seek");
    el.addEventListener("play", onPlay);
    el.addEventListener("pause", onPause);
    el.addEventListener("seeked", onSeeked);
    const off = sync.listenMedia(id, ({ action, time }) => {
      quietUntil.current = Date.now() + 500;
      if (Number.isFinite(time) && Math.abs(el.currentTime - time) > 0.4) el.currentTime = time;
      if (action === "play" && el.paused) el.play().then(() => setBlocked(false)).catch(() => setBlocked(true));
      if (action === "pause" && !el.paused) el.pause();
    });
    return () => {
      el.removeEventListener("play", onPlay);
      el.removeEventListener("pause", onPause);
      el.removeEventListener("seeked", onSeeked);
      off();
    };
  }, [sync, id, ref]);
  return blocked;
}

// Результат слайда: скільки правильно / всього / скільки відповідей дано
export function useReport(slideId, correct, total, answered) {
  const ctx = useContext(AnswersContext);
  useEffect(() => {
    ctx?.report(slideId, { correct, total, answered });
  }, [ctx, slideId, correct, total, answered]);
}

// Учитель дивиться здане ДЗ (лише перегляд): блоки показують історію спроб учня
export function useReviewMode() {
  const ctx = useContext(AnswersContext);
  return Boolean(ctx?.review);
}

// Додати неправильну спробу в історію (для ДЗ: учитель бачить, з якої спроби учень дійшов до відповіді)
export const addTry = (tries, key, value) => ({ ...tries, [key]: [...(tries[key] || []), value] });
